"use client";
// ONE Idempotency-Key per user intent. A send or a move that is clicked twice, or retried after a network error, must reach
// the API as the same key (ADR-041: the second is a replay, not a second write). The key lives from the moment the intent starts until a
// definitive success; a failure keeps it (a retry is the same intent); a different input is a different intent and gets its own key.
// `runOnce` is the only place a key is held. lib/apiCall.ts and the useActionKey hook go through it.
import { useCallback, useEffect, useRef, useState } from "react";

/** A key for one user action (a UUID). */
export function newActionKey(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === "function") return c.randomUUID();
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2)}-${Math.random().toString(16).slice(2)}`;
}

type Held = { key: string; pending: Promise<unknown> | null };
const held = new Map<string, Held>();
const MAX_HELD = 200;

/**
 * Run `fn(key)` for the intent named by `intent` (what is being done to what, with the input that matters: "hold:<job>:<reason>").
 * - a second call for the same intent while the first is in flight gets the first's promise: one request, one key;
 * - after a failure the key is kept, so a retry sends the same key;
 * - after a success the key is cleared, so the next time the user does it, it is a new action;
 * - another intent (the input changed) holds another key.
 */
export function runOnce<T>(intent: string, fn: (key: string) => Promise<T>): Promise<T> {
  let h = held.get(intent);
  if (h?.pending) return h.pending as Promise<T>;
  if (!h) {
    h = { key: newActionKey(), pending: null };
    held.set(intent, h);
    if (held.size > MAX_HELD) {
      // the oldest intent that is not in flight (an in-flight one must keep sharing its call)
      for (const [k, v] of held)
        if (v.pending === null && v !== h) {
          held.delete(k);
          break;
        }
    }
  }
  const entry = h;
  const p = (async () => fn(entry.key))().then(
    (v) => {
      if (held.get(intent) === entry) held.delete(intent);
      return v;
    },
    (e) => {
      entry.pending = null;
      throw e;
    }
  );
  entry.pending = p;
  return p;
}

/** Forget every held key (tests). */
export function clearActionKeys(): void {
  held.clear();
}

/**
 * The hook for a screen that owns an action (a dialog's button): `run(intent, fn)` holds one key across double-clicks and retries,
 * and `busy` is true while it is in flight, to disable the button. Put the WHOLE action (request and its follow-ups) inside `fn`: a joined
 * second click shares the first's promise, so the follow-ups run once.
 */
export function useActionKey() {
  const [busy, setBusy] = useState(0);
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const run = useCallback(<T>(intent: string, fn: (key: string) => Promise<T>): Promise<T> => {
    setBusy((n) => n + 1);
    return runOnce(intent, fn).finally(() => {
      if (mounted.current) setBusy((n) => n - 1);
    });
  }, []);
  return { run, busy: busy > 0 };
}
